<template>
  <div class="box">
    <v-row>
      <v-col cols="12">
        <v-expansion-panels :value="panel" accordion multiple hover tile readonly>
          <v-expansion-panel :style="upperPanelHeight">
            <v-expansion-panel-header hide-actions>Role</v-expansion-panel-header>
            <v-expansion-panel-content>
              <!-- eslint-disable-next-line prettier/prettier -->
              <tree-data-table
                :headers="roleHeaders" :items="roleTree" item-key="label" :height="upperTableHeight"
                @click:row="arrangeAuthTree"
                dense hide-default-footer disable-pagination fixed-header single-select>
                <template #item.isComposite="{ value }"> {{ value == 'C' ? 'Composite' : 'Single' }} </template>
                <template #item.statusColor="{ item }">
                  <v-tooltip content-class="tooltip" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" :style="cssStatusColor(item.statusColor)" small>mdi-circle</v-icon>
                    </template>
                    <p>
                      <span style="font-weight:bold" small>{{ $t('rtrck.field.title.role.status') }}</span>
                    </p>
                    <span small>{{ item.statusMessage }}</span>
                  </v-tooltip>
                </template>
                <template #item.userStatusColor="{ item }">
                  <v-tooltip content-class="tooltip" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" :style="cssStatusColor(item.userStatusColor)" small>mdi-circle</v-icon>
                    </template>
                    <p>
                      <span style="font-weight:bold" small>{{ $t('rtrck.field.title.role.user.status') }}</span>
                    </p>
                    <span small>{{ item.userStatusMessage }}</span>
                  </v-tooltip>
                </template>
                <template #item.changeDatim="{ value }"> {{ value | format('yyyy-MM-dd') }} </template>
                <template #item.assignDatim="{ value }"> {{ value | format('yyyy-MM-dd') }} </template>
                <template #item.period="{ item }"> {{ item.fromDatim | format('yyyy-MM-dd') }} ~ {{ item.toDatim | format('yyyy-MM-dd') }} </template>
              </tree-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel :style="lowerPanelHeight">
            <v-expansion-panel-header hide-actions>Authorization</v-expansion-panel-header>
            <v-expansion-panel-content>
              <!-- eslint-disable-next-line prettier/prettier -->
              <tree-data-table
                :headers="authHeaders" :items="authTree" item-key="label" :height="lowerTableHeight" :loading="loading"
                @click:expand="expandAuthNode"
                dense hide-default-footer disable-pagination fixed-header single-select>
                <template #item.label="{ item }">
                  <v-tooltip content-class="tooltip" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span>{{ item.label }}</span>
                      <svg v-show="item.lampColor" width="36" height="13" v-bind="attrs" v-on="on" style="padding-top:1px;margin-right:10px">
                        <circle cx="6.5" cy="6.5" r="5" :stroke="$vuetify.theme.isDark ? '#888888' : '#000000'" stroke-width="1" :fill="cssRFill(item.lampColor)" />
                        <circle cx="16.5" cy="6.5" r="5" :stroke="$vuetify.theme.isDark ? '#888888' : '#000000'" stroke-width="1" :fill="cssYFill(item.lampColor)" />
                        <circle cx="26.5" cy="6.5" r="5" :stroke="$vuetify.theme.isDark ? '#888888' : '#000000'" stroke-width="1" :fill="cssGFill(item.lampColor)" />
                      </svg>
                    </template>
                    <span small>{{ getLampTip(item.lampColor) }}</span>
                  </v-tooltip>
                </template>
                <template #item.status="{ item }">
                  <span :style="cssStatus(item.deleted)" small>{{ getStatusText(item) }}</span>
                </template>
                <template #item.descrMap="{ value }">
                  <span :style="cssDescription(value.foreColor)" small>{{ value.text }}</span>
                </template>
              </tree-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { api } from '@/api/common/instances';
import { isEmpty } from 'lodash';
import share from './searchShare.js';
export default {
  props: {
    height: {
      type: Number,
    },
    roleTree: {
      type: Array,
    },
  },
  mixins: [share],
  data() {
    return {
      // data 'panel'은 내부의 panel 모두가 디폴트로 펼쳐져서 content가 보이게 하기 위한 조치.
      panel: [0, 1],
      authHeaders: [
        { text: 'Class/Object/Auth/Filed', value: 'label', width: '25%', expandable: true, sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Description', value: 'descrMap', sortable: false },
        { text: 'From', value: 'formattedLowText', sortable: false },
        { text: 'To', value: 'formattedHighText', sortable: false },
      ],
      authTree: [],
      loading: false,
    };
  },
  methods: {
    async arrangeAuthTree(roleNode, { select, selected }) {
      select(!selected);
      let param = {};
      param.svrYmdhms = roleNode.svrYmdhms;
      param.sstmId = roleNode.sstmId;
      param.mandt = roleNode.mandt;
      param.agrName = roleNode.agrName;
      param.dtype = 'I'; // 최초(Init...)의 의미로 'I'를 부여한다.
      this.authTree = await this.getRoleAuthTree(param);
    },
    async expandAuthNode(node, { add }) {
      if (node._opened) return;
      let roleAuthTree = await this.getRoleAuthTree(node);
      // 현재 tree는 expand 시 추가되는 자식은 순서를 거꾸로 해주어야 한다.2021.01.11.KWShin.
      roleAuthTree = roleAuthTree.reverse();
      add(roleAuthTree);
    },
    async getRoleAuthTree(node) {
      this.loading = true;
      const { data } = await api.get('/rtrck/search/role-auth', {
        params: {
          svrYmdhms: node.svrYmdhms,
          sstmId: node.sstmId,
          mandt: node.mandt,
          agrName: node.agrName,
          oclss: node.oclss,
          object: node.object,
          auth: node.auth,
          dtype: node.dtype,
        },
      });
      // RoleAuth는 dtype 속성이 'F'(노드 유형이 '필드')가 아니면 자식 노드를 갖는다.
      data.forEach(elm => {
        elm.children = [];
        if (elm.dtype != 'F') elm.leaf = false;
        else elm.leaf = true;
      });
      // console.log('in RoleAuth getRoleAuthTree()... data=', data);
      this.loading = false;
      return data;
    },
    cssRFill(lampColor) {
      if (lampColor == 'R') return '#FF0000';
      else return '#DCDCDC';
    },
    cssYFill(lampColor) {
      if (lampColor == 'Y') return '#FFFF00';
      else return '#DCDCDC';
    },
    cssGFill(lampColor) {
      if (lampColor == 'G') return '#00FF00';
      else return '#DCDCDC';
    },
    getLampTip(lampColor) {
      if ('G' == lampColor) {
        return this.$t('rtrck.auth.label.maintained');
      } else if ('Y' == lampColor) {
        return this.$t('rtrck.auth.label.missing.value');
      } else if ('R' == lampColor) {
        return this.$t('rtrck.auth.label.missing.variable');
      }
    },
    cssDescription(color) {
      if (!isEmpty(color)) {
        // 원래 서버에서 '#0000FF'를 내려주는데 이는 dark 테마에서 잘 안보인다.
        if (this.$vuetify.theme.isDark) return 'color: #BBBBFF';
        return `color: ${color}`;
      }
    },
    cssStatus(deleted) {
      if (deleted == 'X') {
        if (this.$vuetify.theme.isDark) return 'color: #FF8888';
        return 'color: #FF0000';
      }
      return '';
    },
    getStatusText(item) {
      if (item.deleted == 'X') return this.$t('rtrck.auth.label.inactive');
      else return this.$t(`cd.053.${item.modified}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-expansion-panel-content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>
